import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { Autocomplete, CircularProgress, MenuItem, TextField, } from '@mui/material';
import { useCallback, useEffect, useMemo, useState, } from 'react';
import { useFormContext, } from 'react-hook-form';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { ApiException, useApi } from '@/hooks/useApi';
import useDebouncedFn from '@/hooks/useDebounce';
import usePermissions from '@/hooks/usePermissions';
/* hubspot companies - async search / select
 * search by company name, select sets id
 * if within form provider, input sets "name" */
const HubspotSelect = ({ children, field, fieldState, formState: _, searchParams, owners, slotProps, ...inputProps }) => {
    const { callApi } = useApi();
    const { showError } = useSnackbar();
    const { resetField } = useFormContext() ?? {};
    const { isKasmAdmin } = usePermissions();
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState(new Map());
    const endpoint = useCallback((input) => {
        const params = new URLSearchParams(searchParams);
        if (input)
            params.set('search', input);
        return `/${owners ? 'owners' : 'companies'}/hubspot?${params.toString()}`;
    }, [owners, searchParams]);
    const fetchCompanies = useCallback(async (input) => {
        const params = new URLSearchParams(searchParams);
        if (!owners &&
            (!params.get('owner_id') ||
                params.get('owner_id') == 'null' ||
                params.get('owner_id') == 'undefined')) {
            return;
        }
        try {
            setLoading(true);
            const response = await callApi('GET', endpoint(input));
            if (response) {
                setOptions(response.reduce((acc, o) => acc.set(o.id, o.name), new Map()));
            }
        }
        catch (err) {
            const detail = err instanceof ApiException && err.detail;
            showError(detail || 'errors.generic');
        }
        finally {
            setLoading(false);
        }
    }, [callApi, endpoint, showError, searchParams, owners]);
    const debouncedFetch = useDebouncedFn(fetchCompanies);
    const ownerId = useMemo(() => {
        if (!searchParams)
            return undefined;
        const params = new URLSearchParams(searchParams);
        return params.get('owner_id');
    }, [searchParams]);
    useEffect(() => {
        resetField?.(field.name);
        setOptions(new Map());
    }, [ownerId, resetField, field.name]);
    const errorMessage = useMemo(() => {
        return fieldState?.error?.message ?? '';
    }, [fieldState?.error?.message]);
    return (_jsx(Autocomplete, { ...field, className: "hubspot-select-form-control", "data-testid": "hubspot-select-form-control", disabled: inputProps.disabled, onChange: (_, value) => {
            field.onChange(value);
        }, filterOptions: x => x, open: open, onOpen: () => {
            setOpen(true);
            debouncedFetch();
        }, onClose: () => setOpen(false), onInputChange: (_, input) => debouncedFetch(input), getOptionLabel: option => options.get(option) ?? '', options: [...options.keys()], loading: loading, renderOption: ({ key, ...props }, id) => (_createElement(MenuItem, { ...props, key: key ?? id, className: "owner-select-option", "data-testid": "owner-select-option", dense: true },
            options.get(id),
            isKasmAdmin && _jsx("small", { children: id }))), renderInput: params => (_jsx(TextField, { ...params, className: "hubspot-select-text-field", "data-testid": "hubspot-select-text-field", error: !!errorMessage, helperText: errorMessage || (isKasmAdmin && field.value), label: children, size: "small", ...inputProps, slotProps: {
                ...slotProps,
                // @ts-expect-error data-testid is valid
                formHelperText: { ['data-testid']: 'hubspot-select-helper-text' },
                input: {
                    ...params.InputProps,
                    endAdornment: (_jsxs(_Fragment, { children: [loading && _jsx(CircularProgress, { size: 20 }), params.InputProps.endAdornment] })),
                },
            }, sx: {
                '.MuiFormHelperText-root': { marginBottom: '-24px !important' },
            } })) }));
};
export default HubspotSelect;
